import React, { useEffect, useState } from "react";
import clsx from "clsx";
import {
  Button,
  IconButton,
  Paper,
  Typography,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

import useProspectSession from "src/hooks/useProspectSession";
import {
  PrivacyPolicyUrl,
  readConsent,
  setConsent,
  subscribeConsent,
} from "src/utils/consent";
import { isProspect } from "src/utils/meta";

// The pages a visitor sees before having an account; the same opt-out as the
// landing pages' cookie bar.
const ConsentPages = ["/login", "/signup", "/forgotpassword"];

const useStyles = makeStyles((theme) => ({
  root: {
    position: "fixed",
    right: theme.spacing(2),
    bottom: theme.spacing(2),
    zIndex: theme.zIndex.snackbar,
  },
  // Clears the Zendesk launcher, which logged-in users have bottom right.
  aboveSupportWidget: {
    bottom: theme.spacing(12),
  },
  launcher: {
    borderRadius: 999,
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[4],
    textTransform: "none",
    "&:hover": { background: theme.palette.background.paper },
  },
  card: {
    maxWidth: 380,
    padding: theme.spacing(2),
    borderRadius: 16,
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
}));

/**
 * The opt-out for everyone the Meta Pixel would track: visitors on the login /
 * sign up pages, and logged-in users who haven't subscribed yet.
 */
export default function CookieConsent() {
  const classes = useStyles();
  const session = useProspectSession();
  const [expanded, setExpanded] = useState(false);
  const [consent, setConsentState] = useState(readConsent);

  useEffect(
    () =>
      subscribeConsent(() => {
        setConsentState(readConsent());
      }),
    []
  );

  const { loggedIn, pathname } = session;
  if (!isProspect(session)) return null;
  if (!loggedIn && !ConsentPages.includes(pathname)) return null;

  const choose = (granted: boolean) => {
    setConsent(granted);
    setExpanded(false);
  };

  return (
    <div className={clsx(classes.root, loggedIn && classes.aboveSupportWidget)}>
      {expanded ? (
        <Paper
          className={classes.card}
          elevation={8}
          role="dialog"
          aria-label="Cookie preferences"
        >
          <div className={classes.header}>
            <Typography variant="body2" color="textSecondary">
              <strong>We use cookies.</strong> We use Google Analytics and the
              Meta Pixel to measure traffic and how our ads perform, and to
              improve My Reseller Genie. You can opt out anytime, and it
              won&apos;t affect how the app works.{" "}
              <a
                href={PrivacyPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              {consent === "denied" ? " · You're currently opted out." : null}
            </Typography>
            <IconButton
              size="small"
              aria-label="Close"
              onClick={() => setExpanded(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <div className={classes.actions}>
            <Button variant="outlined" onClick={() => choose(false)}>
              Opt out of cookies
            </Button>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              onClick={() => choose(true)}
            >
              Accept
            </Button>
          </div>
        </Paper>
      ) : (
        <Button
          className={classes.launcher}
          aria-label="Cookie preferences"
          onClick={() => setExpanded(true)}
        >
          Cookies
        </Button>
      )}
    </div>
  );
}
